import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  applyCommit as apiApplyCommit,
  fetchSessionMeta,
  fetchSessionState,
} from "../lib/api";
import type {
  CommitPayloadMap,
  CommitType,
  SessionState,
} from "../types";

interface UseSessionResult {
  state: SessionState | null;
  loading: boolean;
  error: string | null;
  commit: <T extends CommitType>(
    type: T,
    payload: CommitPayloadMap[T]
  ) => Promise<void>;
  clearError: () => void;
  lastSyncedAt: Date | null;
}

const POLL_ACTIVE_MS = 5000;
const POLL_IDLE_MS = 30000;
const IDLE_THRESHOLD_MS = 60_000;

const useSession = (sessionId: string | undefined): UseSessionResult => {
  const [state, setState] = useState<SessionState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  const isFetchingRef = useRef(false);
  const currentUpdatedAtRef = useRef<string | null>(null);
  const pollTimeoutRef = useRef<number | null>(null);
  const pollIntervalRef = useRef<number>(POLL_ACTIVE_MS);
  const lastActivityRef = useRef<number>(Date.now());

  const clearPendingPoll = useCallback(() => {
    if (pollTimeoutRef.current) {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const loadSession = useCallback(
    async (showSpinner: boolean) => {
      if (!sessionId) {
        setState(null);
        setError("Missing session id");
        if (showSpinner) {
          setLoading(false);
        }
        return;
      }

      if (isFetchingRef.current) {
        return;
      }

      if (showSpinner) {
        setLoading(true);
      }

      isFetchingRef.current = true;
      try {
        const session = await fetchSessionState(sessionId);
        setState(session);
        currentUpdatedAtRef.current = session.updatedAt;
        setError(null);
        setLastSyncedAt(new Date());
        lastActivityRef.current = Date.now();
        pollIntervalRef.current = POLL_ACTIVE_MS;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load session";
        setError(message);
      } finally {
        isFetchingRef.current = false;
        if (showSpinner) {
          setLoading(false);
        }
      }
    },
    [sessionId]
  );

  const checkForUpdates = useCallback(async () => {
    if (!sessionId) {
      return;
    }
    try {
      const meta = await fetchSessionMeta(sessionId);
      if (!currentUpdatedAtRef.current || meta.updatedAt !== currentUpdatedAtRef.current) {
        await loadSession(false);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to refresh session";
      setError(message);
    }

    const now = Date.now();
    const idle =
      document.visibilityState === "hidden" ||
      now - lastActivityRef.current > IDLE_THRESHOLD_MS;
    pollIntervalRef.current = idle ? POLL_IDLE_MS : POLL_ACTIVE_MS;
  }, [sessionId, loadSession]);

  const schedulePolling = useCallback(() => {
    clearPendingPoll();
    const nextInterval = pollIntervalRef.current;
    pollTimeoutRef.current = window.setTimeout(async () => {
      await checkForUpdates();
      schedulePolling();
    }, nextInterval);
  }, [checkForUpdates, clearPendingPoll]);

  useEffect(() => {
    clearPendingPoll();
    if (!sessionId) {
      setLoading(false);
      setState(null);
      return;
    }

    loadSession(true).finally(() => {
      schedulePolling();
    });

    return () => {
      clearPendingPoll();
    };
  }, [sessionId, loadSession, schedulePolling, clearPendingPoll]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        pollIntervalRef.current = POLL_IDLE_MS;
      } else {
        lastActivityRef.current = Date.now();
        pollIntervalRef.current = POLL_ACTIVE_MS;
        schedulePolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [schedulePolling]);

  const commit = useCallback(
    async <T extends CommitType>(
      type: T,
      payload: CommitPayloadMap[T]
    ): Promise<void> => {
      if (!sessionId) {
        setError("Missing session id");
        return;
      }
      try {
        const updated = await apiApplyCommit(sessionId, { type, payload });
        setState(updated);
        currentUpdatedAtRef.current = updated.updatedAt;
        setError(null);
        setLastSyncedAt(new Date());
        lastActivityRef.current = Date.now();
        pollIntervalRef.current = POLL_ACTIVE_MS;
        schedulePolling();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to apply changes";
        setError(message);
      }
    },
    [sessionId, schedulePolling]
  );

  const clearError = useCallback(() => setError(null), []);

  return useMemo(
    () => ({
      state,
      loading,
      error,
      commit,
      clearError,
      lastSyncedAt,
    }),
    [state, loading, error, commit, clearError, lastSyncedAt]
  );
};

export default useSession;
